import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'
import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { useMemo } from 'react'
import { permissionCheck } from '../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderBoolean, CustomRenderString, DeleteRow, EditRow } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { OnRowClick } from '../../../baseComponents/TwinTable/VirtualTableListing/types'
import locations from '../../../utils/globals/locations'
import useEditDeleteModal from '../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { EmployeesPayload } from '../../../utils/reducers/company/employees'
import { getAllClientManagerEmployees, getConfigParam } from '../../../utils/reducers/getters'
import ModalChangeActiveCustomer from '../ModalChangeActiveCustomerCustomer'

type TableCustomersB2BProps = TableComponentProps & {
    employees: EmployeesPayload
    onRowClick?: OnRowClick
}

const TableCustomersB2B: React.FC<TableCustomersB2BProps> = ({ setTableInstance, employees, userPermissions, tableInstance, onRowClick}) => {
    const { t } = useTwinTranslation()
    const myEmployees = useMemo(() => getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}, [employees])
    const { openModal, setOpenModal } = useEditDeleteModal()

    const columns: ColumnTableSchema<CustomerModelType> = [
        {
            id: 'code',
            dataKey: 'code',
            label: t('code', 'Código'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerCode', 'Código de cliente') }
            },
            searchKey: 'Customer.code'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('businessName', 'Nombre comercial'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('businessName', 'Nombre comercial') }
            },
            searchKey: 'Customer.name'
        },
        {
            id: 'fiscal_name',
            dataKey: 'fiscal_name',
            label: t('fiscalName', 'Nombre Fiscal'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('fiscalName', 'Nombre Fiscal') }
            }
        },
        {
            id: 'email',
            dataKey: 'email',
            label: t('email', 'Email'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            searchKey: 'Customer.email'
        },
        {
            id: 'phone',
            dataKey: 'phone',
            label: t('phone', 'Teléfono'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('phone', 'Teléfono') }
            },
            searchKey: 'Customer.phone'
        },
        {
            id: 'mobile',
            dataKey: 'mobile',
            label: t('mobile', 'Móvil'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('mobile', 'Móvil') }
            },
            searchKey: 'Customer.mobile'
        },
        {
            id: 'CustomerOriginId',
            dataKey: 'CustomerOriginId',
            label: t('origin', 'Origen'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerOrigin?.name} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('origin', 'Origen') }
            },
            searchKey: 'CustomerOrigin.name',
            hideColumnByDefault: true
        },
        {
            id: 'ResponsibleEmployeeId',
            dataKey: 'ResponsibleEmployeeId',
            label: t('ourManager', 'Nuestro gestor'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.ResponsibleEmployee?.fullname_short} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: myEmployees, label: t('ourManager', 'Nuestro gestor'), fieldName: 'fullname_short' }
            },
            sortable: true,
            searchSingleKey: 'ResponsibleEmployeeId',
            searchKey: 'fullname_short',
            hideColumnByDefault: true
        },
        {
            id: 'responsable_name',
            dataKey: 'responsable_name',
            label: t('responsibleNameShort', 'N. del responsable'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('responsibleNameShort', 'N. del responsable') }
            },
            hideColumnByDefault: true
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'Customer.active',
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpenModal({ type: 'delete', allRowData })} icon={!allRowData?.active ? faRotateLeft : undefined} messageTooltip={!allRowData?.active ? t('reactivate', 'Reactivar') : undefined} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.customerB2B, ['$id', 'main']]} name='customersB2B' columns={columns} getDataFrom='/api/app/customer/getAllCustomers' extraWhereParams={{ 'customer_type': 1 }} setTableInstance={setTableInstance} onRowClick={onRowClick}>
            {openModal?.type === 'delete' ? <ModalChangeActiveCustomer {...{ setOpened: () => setOpenModal(null) }} allRowData={openModal.allRowData} onSubmit={() => tableInstance?.getTableDataFromStart()} /> : null}
        </TwinTableLink>
    )
}
export default TableCustomersB2B